import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText, FolderOpen, Search, Stethoscope, CalendarDays, Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import type { PatientResume } from '../lib/types'
import * as f from '../lib/format'
import { CATEGORIES_DONNEE, TYPES_DOCUMENT, TYPES_VENUE } from '@doc/shared'

type Resultats = {
  patients: PatientResume[]
  documents: { id: string; titre: string; type: string; dateDocument: string | null; patient: PatientResume }[]
  donnees: { id: string; categorie: string; libelle: string; patient: PatientResume }[]
  venues: { id: string; date: string; type: string; motif: string | null; patient: PatientResume }[]
}

export default function BarreRecherche() {
  const [saisie, setSaisie] = useState('')
  const [terme, setTerme] = useState('')
  const [ouvert, setOuvert] = useState(false)
  const naviguer = useNavigate()
  const conteneur = useRef<HTMLDivElement>(null)

  // On attend que la frappe se calme avant d'interroger l'API.
  useEffect(() => {
    const minuteur = setTimeout(() => setTerme(saisie.trim()), 250)
    return () => clearTimeout(minuteur)
  }, [saisie])

  useEffect(() => {
    const surClic = (e: MouseEvent) => {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false)
    }
    document.addEventListener('mousedown', surClic)
    return () => document.removeEventListener('mousedown', surClic)
  }, [])

  const { data, isFetching } = useQuery({
    queryKey: ['recherche', terme],
    queryFn: () => api<Resultats>(`/api/recherche?q=${encodeURIComponent(terme)}`),
    enabled: terme.length >= 2,
  })

  const aller = (chemin: string) => {
    setOuvert(false)
    setSaisie('')
    naviguer(chemin)
  }

  const rien =
    data &&
    data.patients.length === 0 &&
    data.documents.length === 0 &&
    data.donnees.length === 0 &&
    data.venues.length === 0

  return (
    <div className="relative w-full max-w-[640px]" ref={conteneur}>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-brume" />
        <input
          className="champ pl-9"
          placeholder="Rechercher un patient, un document, un diagnostic, un traitement…"
          value={saisie}
          onChange={(e) => {
            setSaisie(e.target.value)
            setOuvert(true)
          }}
          onFocus={() => setOuvert(true)}
        />
        {isFetching && (
          <Loader2 className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin text-brume" />
        )}
      </div>

      {ouvert && terme.length >= 2 && (
        <div
          className="apparition absolute top-[calc(100%+6px)] left-0 z-40 max-h-[70vh] w-full overflow-y-auto rounded-xl bg-white py-2"
          style={{ boxShadow: 'var(--shadow-flottant)' }}
        >
          {rien && <p className="px-4 py-3 text-[13px] text-brume">Aucun résultat.</p>}

          {data && data.patients.length > 0 && (
            <Groupe titre="Dossiers">
              {data.patients.map((p) => (
                <Ligne
                  key={p.id}
                  icone={<FolderOpen className="h-4 w-4 text-bleu-400" />}
                  principal={p.affichage}
                  secondaire={`${p.reference}${
                    p.dateNaissance ? ` · née le ${f.dateCourte(p.dateNaissance)}` : ''
                  }`}
                  surClic={() => aller(`/patients/${p.id}`)}
                />
              ))}
            </Groupe>
          )}

          {data && data.donnees.length > 0 && (
            <Groupe titre="Données médicales">
              {data.donnees.map((d) => (
                <Ligne
                  key={d.id}
                  icone={<Stethoscope className="h-4 w-4 text-bleu-400" />}
                  principal={d.libelle}
                  secondaire={`${
                    CATEGORIES_DONNEE[d.categorie as keyof typeof CATEGORIES_DONNEE] ?? d.categorie
                  } · ${d.patient.affichage}`}
                  surClic={() => aller(`/patients/${d.patient.id}`)}
                />
              ))}
            </Groupe>
          )}

          {data && data.documents.length > 0 && (
            <Groupe titre="Documents">
              {data.documents.map((d) => (
                <Ligne
                  key={d.id}
                  icone={<FileText className="h-4 w-4 text-bleu-400" />}
                  principal={d.titre}
                  secondaire={`${
                    TYPES_DOCUMENT[d.type as keyof typeof TYPES_DOCUMENT] ?? d.type
                  } · ${d.patient.affichage}`}
                  surClic={() => aller(`/patients/${d.patient.id}?document=${d.id}`)}
                />
              ))}
            </Groupe>
          )}

          {data && data.venues.length > 0 && (
            <Groupe titre="Venues">
              {data.venues.map((v) => (
                <Ligne
                  key={v.id}
                  icone={<CalendarDays className="h-4 w-4 text-bleu-400" />}
                  principal={v.motif ?? TYPES_VENUE[v.type as keyof typeof TYPES_VENUE]}
                  secondaire={`${f.dateCourte(v.date)} · ${v.patient.affichage}`}
                  surClic={() => aller(`/patients/${v.patient.id}`)}
                />
              ))}
            </Groupe>
          )}
        </div>
      )}
    </div>
  )
}

function Groupe({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-1 pb-1 text-[11px] font-medium tracking-wide text-brume uppercase">
        {titre}
      </div>
      {children}
    </div>
  )
}

function Ligne({
  icone,
  principal,
  secondaire,
  surClic,
}: {
  icone: React.ReactNode
  principal: string
  secondaire: string
  surClic: () => void
}) {
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-bleu-50"
      onClick={surClic}
    >
      {icone}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] text-encre">{principal}</span>
        <span className="block truncate text-[12px] text-brume">{secondaire}</span>
      </span>
    </button>
  )
}
